import { useEffect, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import {
  Link,
  createFileRoute,
  redirect,
  useNavigate,
} from "@tanstack/react-router";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import Button from "@/components/button";
import Input from "@/components/input";
import PasswordInput from "@/components/password-input";
import { useAuth } from "@/context/auth";
import { LoginSchema } from "@/schemas";
import { decideNextRoute } from "@/utils/auth-flow";
import { loginErrorMessage } from "@/utils/login-error";

export const Route = createFileRoute("/auth/login")({
  beforeLoad: ({ context }) => {
    if (context.auth?.isAuthenticated && context.auth.user) {
      const user = context.auth.user;
      const userRole = user.role;

      const nextRoute = decideNextRoute({
        role: user.role,
        emailVerified: user.emailVerified,
        phoneVerified: user.phoneVerified,
        onboardingCompleted: user.onboardingCompleted,
      });

      if (nextRoute.startsWith("/app/")) {
        throw redirect({
          to: nextRoute,
          params: { role: userRole },
        });
      } else {
        throw redirect({ to: nextRoute });
      }
    }
  },
  component: LoginPage,
});

function LoginPage() {
  const { t } = useTranslation("auth");
  const auth = useAuth();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (auth.isAuthenticated && auth.user) {
      const role = auth.user.role;
      const nextRoute = decideNextRoute({
        role,
        emailVerified: auth.user.emailVerified,
        phoneVerified: auth.user.phoneVerified,
        onboardingCompleted: auth.user.onboardingCompleted,
      });
      if (nextRoute.startsWith("/app/")) {
        navigate({ to: nextRoute, params: { role } });
      } else {
        navigate({ to: nextRoute });
      }
    }
  }, [auth.isAuthenticated, auth.user, navigate]);
  const methods = useForm({
    resolver: zodResolver(LoginSchema),
  });

  const handleSubmit = methods.handleSubmit(async (data) => {
    setErrorMessage(null);
    try {
      await auth.login(data);
    } catch (err: unknown) {
      setErrorMessage(loginErrorMessage(err, t));
    }
  });
  return (
    <main className="min-h-screen w-full bg-cocoa grid grid-cols-1 md:grid-cols-2">
      <div className="left bg-linear-26 from-cocoa to-ember h-full flex items-center justify-center">
        <img
          src="/bg-pattern.png"
          alt="Pattern"
          className="w-full object-cover max-h-[200px] md:max-h-none h-auto px-4"
        />
      </div>
      <div className="flex flex-col items-left justify-center px-10 lg:px-[70px] gap-[21px] bg-white">
        <div className="mb-8 flex items-start gap-3">
          <img src="/newlogo.svg" alt="Public Market" height={40} className="h-10 w-auto" />
          <p className="body hidden md:block">
            {t("login.taglineLine1")}
            <br />
            {t("login.taglineLine2")}
          </p>
        </div>
        <h1 className="heading-1">{t("login.logInTitle")}</h1>
        <FormProvider {...methods}>
          <form
            className="flex flex-col gap-4 max-w-[320px]"
            onSubmit={handleSubmit}
          >
            <Input
              type="email"
              label={t("login.emailLabel")}
              required
              {...methods.register("email")}
            />
            <PasswordInput
              label={t("login.passwordLabel")}
              placeholder={t("login.passwordPlaceholder")}
              required
              {...methods.register("password")}
            />
            <Link
              className="text-right body-small font-bold"
              to="/auth/forgot-password"
            >
              {t("login.forgotPasswordLink")}
            </Link>
            <Button
              variant="secondary"
              className="w-[320px]"
              type="submit"
              data-testid="login-button"
            >
              {t("login.submitButton")}
            </Button>
            {errorMessage && (
              <p className="body-small text-red-500">{errorMessage}</p>
            )}
          </form>
        </FormProvider>
        <Button variant="ghost" className="w-[320px]" asChild>
          <Link to="/static/quick-help">{t("login.iNeedHelp")}</Link>
        </Button>
        <p className="body-small">
          {t("login.notOnPM")}{" "}
          <Link to="/auth/signup" className="font-bold">
            {t("login.createAccount")}
          </Link>
        </p>
      </div>
    </main>
  );
}
